import {
  Box,
  Col,
  Flex,
  Form,
  NumericFormatField,
  RadioGroupField,
  Row,
  SelectField,
  SubmitButton,
  Text,
  TextAreaInputField,
  TitledText,
} from '@/components';
import { formatOwnershipRatioFraction } from '@/features/mold/utils';
import {
  ExistenceOrAbsenceLabelModifier,
  InspectionEvaluationKsFormSchema,
  OperationTypeLabelModifier,
  operationType,
} from '@/features/project/projects/_models/project';
import { isBefore2025 } from '@/features/project/review-sheet/utils/project';
import { useForm } from '@/hooks';
import { booleanStringType } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

const existenceOrAbsenceOptions = booleanStringType.options.map((value) => ({
  value,
  label: ExistenceOrAbsenceLabelModifier[value],
}));

type FormValues = z.infer<typeof InspectionEvaluationKsFormSchema>;
type InitialFormValues = Partial<FormValues>;

type Props = {
  fiscalYear: number;
  initialValues?: InitialFormValues;
  onSubmit: (values: FormValues) => void;
};

export const OwnershipRatioForm: FC<Props> = ({
  fiscalYear,
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<InitialFormValues, FormValues>({
    defaultValues: isBefore2025({ fiscalYear })
      ? initialValues
      : {
          ...initialValues,
          ownershipRatio: {
            ...initialValues?.ownershipRatio,
            operationType: 'auto',
            manualRatio: null,
            basis: {
              ...initialValues?.ownershipRatio?.basis,
              calculationFormula: null,
            },
          },
        },
    resolver: zodResolver(InspectionEvaluationKsFormSchema),
  });

  const [
    ownershipRatioRatioNumerator,
    ownershipRatioRatioDenominator,
    ownershipRatioOperationType,
  ] = watch([
    'ownershipRatio.ratioNumerator',
    'ownershipRatio.ratioDenominator',
    'ownershipRatio.operationType',
  ]);

  useEffect(() => {
    if (ownershipRatioOperationType === 'manual') {
      setValue('ownershipRatio.ratioNumerator', null);
      setValue('ownershipRatio.ratioDenominator', null);
    }
    if (ownershipRatioOperationType === 'auto') {
      setValue('ownershipRatio.manualRatio', null);
    }
  }, [ownershipRatioOperationType, setValue]);

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <>
        <Flex
          align="center"
          justify="space-between"
          style={{ marginBottom: 12 }}
        >
          <Text variant="h5" text={t('ownership-ratio-title')} />
          {isBefore2025({ fiscalYear }) && (
            <RadioGroupField
              name="ownershipRatio.operationType"
              control={control}
              errors={errors}
              options={operationType.options.map((value) => ({
                value,
                label: OperationTypeLabelModifier[value],
              }))}
              marginBottom={0}
            />
          )}
        </Flex>

        <Row gutter={[16, 24]}>
          <Col span={6}>
            {ownershipRatioOperationType === 'auto' ? (
              <TitledText
                title={t('ownership-ratio')}
                text={formatOwnershipRatioFraction(
                  ownershipRatioRatioNumerator,
                  ownershipRatioRatioDenominator,
                )}
              />
            ) : (
              <NumericFormatField
                name="ownershipRatio.manualRatio"
                label={t('ownership-ratio')}
                control={control}
                errors={errors}
                decimalScale={2}
              />
            )}
          </Col>
          {ownershipRatioOperationType === 'auto' && (
            <>
              <Col span={6}>
                <NumericFormatField
                  name="ownershipRatio.ratioNumerator"
                  label={t('ownership-ratio-numerator')}
                  control={control}
                  errors={errors}
                  decimalScale={3}
                />
              </Col>
              <Col span={6}>
                <NumericFormatField
                  name="ownershipRatio.ratioDenominator"
                  label={t('ownership-ratio-denominator')}
                  control={control}
                  errors={errors}
                  decimalScale={3}
                />
              </Col>
            </>
          )}
        </Row>
        {isBefore2025({ fiscalYear }) ? (
          <>
            <TextAreaInputField
              name="ownershipRatio.basis.calculationFormula"
              label={t('basis-calculation-formula')}
              hint={t('hint-basis-calculation-formula')}
              control={control}
              errors={errors}
              rows={1}
            />
            <TextAreaInputField
              name="ownershipRatio.basis.contents"
              label={t('contents-of-each-term')}
              hint={t('hint-contents-of-each-term')}
              control={control}
              errors={errors}
              rows={2}
            />
            <TextAreaInputField
              name="ownershipRatio.conceptOfProjectProspect.calculationFormula"
              label={t('concept-of-basis-calculation-formula')}
              hint={t('hint-concept-of-basis-calculation-formula')}
              control={control}
              errors={errors}
              rows={2}
            />
            <TextAreaInputField
              name="ownershipRatio.conceptOfProjectProspect.contents"
              label={t('concept-of-contents-of-each-term-before-2025')}
              hint={t('hint-concept-of-contents-of-each-term')}
              control={control}
              errors={errors}
              rows={2}
            />
          </>
        ) : (
          <>
            <TextAreaInputField
              name="ownershipRatio.basis.contentsOfHeldFundAmount"
              label={t('contents-of-held-fund-amount-of-ownership-ratio')}
              hint={t('hint-contents-of-held-fund-amount-of-ownership-ratio')}
              control={control}
              errors={errors}
              rows={2}
            />
            <TextAreaInputField
              name="ownershipRatio.basis.contentsOfFundProjectCosts"
              label={t('contents-of-fund-project-costs-of-ownership-ratio')}
              hint={t('hint-contents-of-fund-project-costs-of-ownership-ratio')}
              control={control}
              errors={errors}
              rows={2}
            />
            <TextAreaInputField
              name="ownershipRatio.conceptOfProjectProspect.contents"
              label={t('concept-of-contents-of-each-term')}
              hint={t('hint-concept-of-contents-of-each-term-after-2025')}
              control={control}
              errors={errors}
              rows={2}
            />
          </>
        )}
        <TextAreaInputField
          name="ownershipRatio.conceptOfProjectProspect.basisForCalculationOfIndicators"
          label={t('basis-for-estimating-indicators-for-project-prospect')}
          hint={t('hint-basis-for-estimating-indicators-for-project-prospect')}
          control={control}
          errors={errors}
          rows={2}
        />
        <TextAreaInputField
          name="ownershipRatio.conceptOfProjectProspect.moreRecentResults"
          label={t('indicators-for-project-prospect-and-recent-performance')}
          hint={t(
            'hint-indicators-for-project-prospect-and-recent-performance',
          )}
          control={control}
          errors={errors}
          rows={2}
        />

        <Box marginBottom={12}>
          <Text variant="h5" text={t('low-used-fund-investigation')} />
        </Box>
        <SelectField
          name="lowUsedFundInvestigation.hasCeasedOperations"
          label={t('has-ceased-operations')}
          control={control}
          errors={errors}
          options={existenceOrAbsenceOptions}
        />
        <SelectField
          name="lowUsedFundInvestigation.hasNoResultRecent"
          label={t('has-no-operation-fund-detected')}
          control={control}
          errors={errors}
          options={existenceOrAbsenceOptions}
        />
        <SelectField
          name="lowUsedFundInvestigation.hasFundThatLostPurpose"
          label={t('has-fund-that-lost-purpose')}
          control={control}
          errors={errors}
          options={existenceOrAbsenceOptions}
        />
        <SelectField
          name="lowUsedFundInvestigation.hasExceedOneSignificantly"
          label={t('has-exceed-1-significantly')}
          control={control}
          errors={errors}
          options={existenceOrAbsenceOptions}
        />
        <SelectField
          name="lowUsedFundInvestigation.hasDeemedUnlikelyToBeUsed"
          label={t('has-deemed-unlikely-to-be-used')}
          control={control}
          errors={errors}
          options={existenceOrAbsenceOptions}
        />
        <TextAreaInputField
          name="lowUsedFundInvestigation.reasonsForBeingFalseWhileExceedingOne"
          label={t(
            'reason-for-selecting-absense-when-ownership-ratio-exceeds-1',
          )}
          control={control}
          errors={errors}
          rows={2}
        />
        <TextAreaInputField
          name="lowUsedFundInvestigation.resultsOfInvestigations"
          label={t('consideration-where-the-fund-are-unlikely-to-be-used')}
          hint={t('hint-consideration-where-the-fund-are-unlikely-to-be-used')}
          control={control}
          errors={errors}
          rows={2}
        />
        <TextAreaInputField
          name="lowUsedFundInvestigation.reasonsForLeaving"
          label={t('reason-for-leaving-the-fund-that-are-unlikely-to-be-used')}
          hint={t(
            'hint-reason-for-leaving-the-fund-that-are-unlikely-to-be-used',
          )}
          control={control}
          errors={errors}
          rows={2}
        />
      </>

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
